import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUserCategories } from "../../api-services/user";
import { category } from "../../redux/slices/authSlice";
import { whatsAppMessage } from "../../utils/common"; // ✅ Import WhatsApp handler

const UserDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orgId } = useSelector((state) => state.auth);
  const [categories, setCategories] = useState([]);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const fetchUserCategories = async () => {
    try {
      const res = await getUserCategories(orgId);
      setCategories(res.payload.data);
      console.log("Fetched categories:", res);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  useEffect(() => {
    fetchUserCategories();
  }, []);

  const handleMenuClick = (cat) => {
    dispatch(category({ category: { id: cat?._id, name: cat?.categoryName } }));
    if (cat.isGeneric && cat?.categoryName === "Prescription") {
      navigate("/prescription");
    } else {
      navigate("/files");
    }
  };

  // ✅ WhatsApp handlers for static cards
  const handlePharmacyClick = () => {
    whatsAppMessage("+923210111707", "Hello, I want to order from Pharmacy.");
  };

  const handleHomeServiceClick = () => {
    whatsAppMessage("+923210111707", "Hello, I want to book a home service.");
  };

  const categoryImages = {
    hospital: "/HospitalThumbnail.jpg",
    gym: "/GymThumbnail2.jpg",
    radiology: "/RadiologyThumbnail.jpg",
    pharmacy: "/PharmacyThumbnail.jpg",
    lab: "/LabThumbnail2.jpg",
    prescription: "/PrescriptionThumbnail.jpg",
  };

  return (
    <Container
      component="main"
      maxWidth="lg"
      sx={{
        marginTop: { xs: "2%", md: "5%" },
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Typography
        variant="h4"
        align="center"
        gutterBottom
        sx={{
          fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
          mb: { xs: 2, md: 3 },
        }}
      >
        User Dashboard
      </Typography>
      <Grid
        container
        spacing={{ xs: 2, md: 3 }}
        justifyContent="center"
        direction={isMobile ? "column" : "row"}
        alignItems={isMobile ? "center" : "stretch"}
      >
        {/* --- 1. Get Promo Code Card --- If the organization is not Zeenat Lab, show this */}
        {orgId !== "68f904ae92f8a5d78a184808" && (
          <Grid item xs={12} sm={6} md={4}>
            <Card
              onClick={() => navigate("/assign-promo-code")}
              sx={{
                cursor: "pointer",
                minHeight: { xs: "120px", md: "140px" },
                width: "100%",
                maxWidth: { xs: "100%", sm: "300px", md: "none" },
                position: "relative",
                overflow: "hidden",
                "&:hover": {
                  transform: "scale(1.02)",
                  transition: "transform 0.2s ease-in-out",
                },
              }}
            >
              <Box sx={{ position: "relative" }}>
                <CardMedia
                  component="img"
                  height="140"
                  image="/PromocodeThumbnail.jpg"
                  alt="Get Promo Code"
                  sx={{
                    filter: "brightness(65%)",
                    height: { xs: "120px", md: "140px" },
                  }}
                />
                <Typography
                  variant="h6"
                  color="white"
                  align="center"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontWeight: "bold",
                    fontSize: { xs: "1rem", md: "1.2rem" },
                    px: 1,
                  }}
                >
                  Get Promo Code
                </Typography>
              </Box>
            </Card>
          </Grid>
        )}

        {/* --- 2. Pharmacy Card (STATIC) --- */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            onClick={handlePharmacyClick}
            sx={{
              cursor: "pointer",
              minHeight: { xs: "120px", md: "140px" },
              width: "100%",
              maxWidth: { xs: "100%", sm: "300px", md: "none" },
              position: "relative",
              overflow: "hidden",
              "&:hover": {
                transform: "scale(1.02)",
                transition: "transform 0.2s ease-in-out",
              },
            }}
          >
            <Box sx={{ position: "relative" }}>
              <CardMedia
                component="img"
                height="140"
                image="/PharmacyThumbnail.jpg"
                alt="Pharmacy"
                sx={{
                  filter: "brightness(65%)",
                  height: { xs: "120px", md: "140px" },
                }}
              />
              <Typography
                variant="h6"
                color="white"
                align="center"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontWeight: "bold",
                  fontSize: { xs: "1rem", md: "1.2rem" },
                  px: 1,
                }}
              >
                Pharmacy
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* --- 3. Home Service Card (STATIC) --- */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            onClick={handleHomeServiceClick}
            sx={{
              cursor: "pointer",
              minHeight: { xs: "120px", md: "140px" },
              width: "100%",
              maxWidth: { xs: "100%", sm: "300px", md: "none" },
              position: "relative",
              overflow: "hidden",
              "&:hover": {
                transform: "scale(1.02)",
                transition: "transform 0.2s ease-in-out",
              },
            }}
          >
            <Box sx={{ position: "relative" }}>
              <CardMedia
                component="img"
                height="140"
                image="/HomeServiceThumbnail2.jpg"
                alt="Home Service"
                sx={{
                  filter: "brightness(65%)",
                  height: { xs: "120px", md: "140px" },
                }}
              />
              <Typography
                variant="h6"
                color="white"
                align="center"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontWeight: "bold",
                  fontSize: { xs: "1rem", md: "1.2rem" },
                  px: 1,
                }}
              >
                Home Service
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* --- 4. Patient Lookup Card (STATIC) --- If the organization is Zeenat Lab, show this */}
        {orgId === "68f904ae92f8a5d78a184808" && (
          <Grid item xs={12} sm={4}>
            <Card
              onClick={() => navigate("/patient-lookup")}
              sx={{
                cursor: "pointer",
                minHeight: "140px",
                minWidth: "210px",
                position: "relative",
                overflow: "hidden",
                "&:hover": {
                  transform: "scale(1.02)",
                  transition: "transform 0.2s ease-in-out",
                },
              }}
            >
              <Box sx={{ position: "relative" }}>
                <Card
                  sx={{
                    height: 140,
                    backgroundImage: "url(/websiteAssets/49.png)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "100% 200%", // zoom image vertically x2 (100% → 200%)
                    backgroundPosition: "center 60%", // this selects the 20–70% region
                    filter: "brightness(65%)",
                  }}
                />

                <Typography
                  variant="h6"
                  color="white"
                  align="center"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontWeight: "bold",
                    fontSize: "1.2rem",
                  }}
                >
                  Patient Lookup
                </Typography>
              </Box>
            </Card>
          </Grid>
        )}

        {/* --- 4. Dynamic Category Cards ---
        {categories.map((category, index) => {
          console.log(category, index);
          const imageUrl = category.imageUrl ?? "/Profile.jpeg";
          return (
            <Grid item xs={12} sm={4} key={index}>
              <Card
                onClick={() => handleMenuClick(category)}
                sx={{
                  cursor: "pointer",
                  minHeight: "140px",
                  minWidth: "210px",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    transform: "scale(1.02)",
                    transition: "transform 0.2s ease-in-out",
                  },
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={`/ApnaLogoPrevious.png`}
                    alt={category.categoryName}
                    sx={{ filter: "brightness(70%)" }}
                  />
                  <Typography
                    variant="h6"
                    color="white"
                    align="center"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                    }}
                  >
                    {category.categoryName}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          );
        })} */}

        {categories.map((category, index) => {
          const imageSrc =
            categoryImages[category.categoryName?.toLowerCase()] ||
            "/ApnaLogoPrevious.png";

          return (
            <Grid item xs={12} sm={4} key={index}>
              <Card
                onClick={() => handleMenuClick(category)}
                sx={{
                  cursor: "pointer",
                  minHeight: "140px",
                  minWidth: "210px",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    transform: "scale(1.02)",
                    transition: "transform 0.2s ease-in-out",
                  },
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={imageSrc}
                    alt={category.categoryName}
                    sx={{
                      filter: "brightness(70%)",
                      height: { xs: "120px", md: "140px" },
                    }}
                  />
                  <Typography
                    variant="h6"
                    color="white"
                    align="center"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      fontWeight: "bold",
                      fontSize: { xs: "1rem", md: "1.2rem" },
                      px: 1,
                    }}
                  >
                    {category.categoryName}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default UserDashboard;
